// pages/ProductFormPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  getProductById, 
  createProduct, 
  updateProduct 
} from '../services/productService';
import { getCategories } from '../services/categoryService';
import { getManufacturers } from '../services/manufacturerService';
import { getAllUnits, getWeightUnitDisplayName, getVolumeUnitDisplayName } from '../services/unitsService';
import { getIcon } from '../utils/icons';

/**
 * Форма создания/редактирования товара
 * @returns {JSX.Element} - Компонент для рендеринга
 */
const ProductFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [units, setUnits] = useState({ weight: [], volume: [] });
  
  const [product, setProduct] = useState({
    name: '',
    description: '',
    barcode: '',
    sku: '',
    categoryId: '',
    manufacturerId: '',
    weight: '',
    weightUnit: '',
    volume: '',
    volumeUnit: '',
    dimensions: '',
    isActive: true
  });
  
  const [errors, setErrors] = useState({});
  
  // Загрузка данных при монтировании компонента
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Загрузка категорий
        const categoriesResponse = await getCategories({}, true);
        const categoriesData = Array.isArray(categoriesResponse) ? categoriesResponse : (categoriesResponse.items || categoriesResponse.data || []);
        // Фильтруем только активные категории для выбора
        setCategories(categoriesData.filter(c => c.isActive));
        
        // Загрузка производителей
        const manufacturersResponse = await getManufacturers({}, true);
        const manufacturersData = Array.isArray(manufacturersResponse) ? manufacturersResponse : (manufacturersResponse.items || manufacturersResponse.data || []);
        // Фильтруем только активные производители для выбора
        setManufacturers(manufacturersData.filter(m => m.isActive));
        
        // Загрузка единиц измерения
        const unitsData = await getAllUnits();
        setUnits(unitsData);
        
        // Если режим редактирования, загружаем данные товара
        if (isEditMode) {
          const productData = await getProductById(id);
          setProduct({
            name: productData.name || '',
            description: productData.description || '',
            barcode: productData.barcode || '',
            sku: productData.sku || '',
            categoryId: productData.categoryId || '',
            manufacturerId: productData.manufacturerId || '',
            weight: productData.weight || '',
            weightUnit: productData.weightUnit || '',
            volume: productData.volume || '',
            volumeUnit: productData.volumeUnit || '',
            dimensions: productData.dimensions || '',
            isActive: productData.isActive === undefined ? true : productData.isActive
          });
        }
      } catch (err) {
        console.error('Ошибка при загрузке данных:', err);
        toast.error('Не удалось загрузить необходимые данные');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [id, isEditMode]);
  
  /**
   * Обработка изменения полей формы
   * @param {Object} e - Событие изменения
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProduct({
      ...product,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Сбрасываем ошибку для измененного поля
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  
  /**
   * Валидация формы перед отправкой
   * @returns {boolean} - Результат валидации
   */
  const validateForm = () => {
    const newErrors = {};
    
    if (!product.name.trim()) {
      newErrors.name = 'Название товара обязательно';
    }
    
    // Дополнительные проверки по необходимости
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  /**
   * Отправка формы
   * @param {Object} e - Событие отправки формы
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Пожалуйста, исправьте ошибки в форме');
      return;
    }
    
    setSaving(true);
    
    try {
      // Подготавливаем данные для отправки на сервер
      const productData = {
        ...product,
        categoryId: product.categoryId ? parseInt(product.categoryId, 10) : null,
        manufacturerId: product.manufacturerId ? parseInt(product.manufacturerId, 10) : null,
        weight: product.weight ? parseFloat(product.weight) : null,
        weightUnit: product.weightUnit ? parseInt(product.weightUnit, 10) : null,
        volume: product.volume ? parseFloat(product.volume) : null,
        volumeUnit: product.volumeUnit ? parseInt(product.volumeUnit, 10) : null
      };
      
      if (isEditMode) {
        await updateProduct(id, productData);
        toast.success('Товар успешно обновлен');
      } else {
        await createProduct(productData);
        toast.success('Товар успешно создан');
      }
      
      navigate('/products');
    } catch (err) {
      console.error('Ошибка при сохранении товара:', err);
      toast.error(isEditMode 
        ? 'Не удалось обновить товар' 
        : 'Не удалось создать товар'
      );
    } finally {
      setSaving(false);
    }
  };
  
  /**
   * Отмена редактирования и возврат к списку товаров
   */
  const handleCancel = () => {
    navigate('/products');
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditMode ? 'Редактирование товара' : 'Создание нового товара'}
        </h1>
        <p className="text-gray-600">
          {isEditMode 
            ? 'Измените информацию о товаре и нажмите "Сохранить"' 
            : 'Заполните форму для создания нового товара'
          }
        </p>
      </header>
      
      <div className="bg-white rounded-lg shadow-sm p-6">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Основная информация */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название товара <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={product.name}
                  onChange={handleChange}
                  className={`block w-full rounded-md py-2 px-3 border ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание
                </label>
                <textarea
                  name="description"
                  value={product.description || ''}
                  onChange={handleChange}
                  rows="4"
                  className="block w-full rounded-md py-2 px-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Категория
                </label>
                <select
                  name="categoryId"
                  value={product.categoryId}
                  onChange={handleChange}
                  className="block w-full rounded-md py-2 px-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Выберите категорию</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Производитель
                </label>
                <select
                  name="manufacturerId"
                  value={product.manufacturerId}
                  onChange={handleChange}
                  className="block w-full rounded-md py-2 px-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Выберите производителя</option>
                  {manufacturers.map((manufacturer) => (
                    <option key={manufacturer.id} value={manufacturer.id}>
                      {manufacturer.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Дополнительная информация */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Штрихкод
                </label>
                <input
                  type="text"
                  name="barcode"
                  value={product.barcode || ''}
                  onChange={handleChange}
                  className="block w-full rounded-md py-2 px-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Артикул
                </label>
                <input
                  type="text"
                  name="sku"
                  value={product.sku || ''}
                  onChange={handleChange}
                  className="block w-full rounded-md py-2 px-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Вес и единица измерения веса */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Вес
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    name="weight"
                    value={product.weight || ''}
                    onChange={handleChange}
                    placeholder="Введите вес"
                    className="block w-full rounded-md py-2 px-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    name="weightUnit"
                    value={product.weightUnit || ''}
                    onChange={handleChange}
                    className="block w-full rounded-md py-2 px-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Единица веса</option>
                    {units.weight.map((unit) => (
                      <option key={unit.value} value={unit.value}>
                        {unit.displayName}
                      </option>
                    ))}
                  </select>
                </div>
                {product.weight && product.weightUnit && (
                  <p className="mt-1 text-xs text-gray-500">
                    {product.weight} {getWeightUnitDisplayName(parseInt(product.weightUnit))}
                  </p>
                )}
              </div>
              
              {/* Объем и единица измерения объема */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Объем
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    name="volume"
                    value={product.volume || ''}
                    onChange={handleChange}
                    placeholder="Введите объем"
                    className="block w-full rounded-md py-2 px-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    name="volumeUnit"
                    value={product.volumeUnit || ''}
                    onChange={handleChange}
                    className="block w-full rounded-md py-2 px-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Единица объема</option>
                    {units.volume.map((unit) => (
                      <option key={unit.value} value={unit.value}>
                        {unit.displayName}
                      </option>
                    ))}
                  </select>
                </div>
                {product.volume && product.volumeUnit && (
                  <p className="mt-1 text-xs text-gray-500">
                    {product.volume} {getVolumeUnitDisplayName(parseInt(product.volumeUnit))}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Размеры
                </label>
                <input
                  type="text"
                  name="dimensions"
                  value={product.dimensions || ''}
                  onChange={handleChange}
                  placeholder="Например: 10x15x20 см"
                  className="block w-full rounded-md py-2 px-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {isEditMode && (
                <div className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={product.isActive}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                    Товар активен
                  </label>
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-8 flex items-center justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={saving}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="animate-spin h-4 w-4 mr-2 border-t-2 border-b-2 border-white rounded-full"></span>
                  Сохранение...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="ml-2">Сохранить</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductFormPage;